import React, { useState, useEffect } from 'react';
import ModalDialog from '../components/Dialog/ModalDialog';
import Chip from '../components/Chip/Chip';
import Toggle from '../components/Toggle/Toggle';
import Typography from '../components/Text/Typography';
import Icon from '../components/Icon/Icon';
import LoadingSpinner from '../components/LoadingSpinner';
import './ActiveIngredientsDialog.css';

interface ActiveIngredient {
  active_id: string;
  active_name: string;
  level: 'beginner' | 'intermediate' | 'advanced';
  product_count: number;
}

interface ActiveIngredientsDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onActiveClick?: (active: ActiveIngredient) => void;
}

const ActiveIngredientsDialog: React.FC<ActiveIngredientsDialogProps> = ({
  isOpen,
  onClose,
  onActiveClick
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedFilter, setSelectedFilter] = useState<'all' | 'beginner' | 'intermediate' | 'advanced'>('all');
  const [toggleEnabled, setToggleEnabled] = useState(false);
  const [activeIngredients, setActiveIngredients] = useState<ActiveIngredient[]>([]);
  const [loading, setLoading] = useState(false);

  // Fetch active ingredients data
  useEffect(() => {
    if (isOpen) {
      fetchActiveIngredients();
    }
  }, [isOpen]);

  const fetchActiveIngredients = async () => {
    setLoading(true);
    try {
      const response = await fetch('/api/user/active-levels', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('idToken')}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setActiveIngredients(data);
      } else {
        console.error('Failed to fetch active ingredients');
      }
    } catch (error) {
      console.error('Error fetching active ingredients:', error);
    } finally {
      setLoading(false);
    }
  };


  // Filter active ingredients based on search, level filter, and cabinet toggle
  const filteredActives = activeIngredients.filter(active => {
    const matchesSearch = active.active_name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = selectedFilter === 'all' || active.level === selectedFilter;
    const matchesCabinet = !toggleEnabled || active.product_count > 0;
    return matchesSearch && matchesFilter && matchesCabinet;
  });

  const handleActiveClick = (active: ActiveIngredient) => {
    if (onActiveClick) {
      onActiveClick(active);
    }
  };

  const getLevelColor = (level: string) => {
    switch (level) {
      case 'beginner':
        return 'var(--color-primary-green)';
      case 'intermediate':
        return 'var(--color-warning-yellow)';
      case 'advanced':
        return 'var(--color-warning-red)';
      default:
        return 'var(--color-gray)';
    }
  };

  return (
    <ModalDialog
      isOpen={isOpen}
      onClose={onClose}
      title="Active Leveling"
      maxWidth="640px"
      maxHeight="600px"
    >
      <div className="active-ingredients-dialog">
        {/* Search Bar */}
        <div className="search-section">
          <div className="search-input-wrapper">
            {!searchTerm && (
              <div className="search-icon">
                <Icon name="search" size="small" />
              </div>
            )}
            <input
              type="text"
              placeholder="Search"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={`search-input-field ${searchTerm ? 'active' : ''}`}
            />
            {searchTerm && (
              <button className="clear-search-button" onClick={() => setSearchTerm('')}>
                <Icon name="close" size="small" />
              </button>
            )}
          </div>
        </div>

        {/* Filter Section */}
        <div className="filter-section">
          <div className="filter-header">
            <Typography variant="body-small-bold" className="total-count">
              Total {filteredActives.length}
            </Typography>
            <Toggle
              checked={toggleEnabled}
              onChange={setToggleEnabled}
              label="In my cabinet"
              id="active-ingredients-toggle"
            />
          </div>
          
          <div className="filter-chips">
            <Chip
              label="All"
              isSelected={selectedFilter === 'all'}
              onClick={() => setSelectedFilter('all')}
            />
            <Chip
              label="Beginner"
              isSelected={selectedFilter === 'beginner'}
              onClick={() => setSelectedFilter('beginner')}
            />
            <Chip
              label="Intermediate"
              isSelected={selectedFilter === 'intermediate'}
              onClick={() => setSelectedFilter('intermediate')}
            />
            <Chip
              label="Advanced"
              isSelected={selectedFilter === 'advanced'}
              onClick={() => setSelectedFilter('advanced')}
            />
          </div>
        </div>

        {/* Active Ingredients List */}
        <div className="active-ingredients-list">
          {loading ? (
            <div className="loading-state">
              <LoadingSpinner size="small" text="Loading active ingredients..." />
            </div>
          ) : filteredActives.length === 0 ? (
            <div className="empty-state">
              <Typography variant="body">No active ingredients found.</Typography>
            </div>
          ) : (
            filteredActives.map((active) => (
              <div
                key={active.active_id}
                className="active-ingredient-item"
                onClick={() => handleActiveClick(active)}
              >
                <div className="active-ingredient-content">
                  <Typography variant="body-bold" className="active-name">
                    {active.active_name}
                  </Typography>
                  <Typography variant="body-small" className="product-count">
                    {active.product_count > 0 
                      ? `Included in ${active.product_count} product${active.product_count !== 1 ? 's' : ''}`
                      : 'Not in cabinet'
                    }
                  </Typography>
                </div>
                
                <div className="active-ingredient-right">
                  <div
                    className="level-tag"
                    style={{ backgroundColor: getLevelColor(active.level) }}
                  >
                    <Typography variant="body-small" className="level-text">
                      {active.level.charAt(0).toUpperCase() + active.level.slice(1)}
                    </Typography>
                  </div>
                  <Icon name="chevron-right" size="small" className="chevron-icon" />
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </ModalDialog>
  );
};

export default ActiveIngredientsDialog;
